import { memo, useCallback } from 'react'
import { Box, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { StorePerformanceItem } from '../types'
import { useVersionListStyles } from '../styles/versionListStyles'

interface SelectedQuestionnairesForVersionsProps {
  questionnaires: StorePerformanceItem[]
  activeQuestionnaireId: string | null
  onSelectQuestionnaire: (id: string) => void
  t: (key: string, options?: Record<string, unknown>) => string
}

const SelectedQuestionnairesForVersions = ({
  questionnaires,
  activeQuestionnaireId,
  onSelectQuestionnaire,
  t,
}: SelectedQuestionnairesForVersionsProps) => {
  const styles = useVersionListStyles()

  const handleClick = useCallback(
    (id: string) => {
      onSelectQuestionnaire(id)
    },
    [onSelectQuestionnaire]
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={styles.listColumnHeader}>
        <Typography>
          {t('dashboard.storePerformance.selectedQuestionnaires')}
        </Typography>
      </Box>

      <Box sx={styles.listContainer}>
        {questionnaires.map((q) => {
          const isActive = q.id === activeQuestionnaireId
          const executionCount = q.versions?.length ?? 0

          return (
            <Box
              key={q.id}
              sx={{
                ...styles.listItemRow,
                border: `1px solid ${isActive ? styles.colors.color45 : `${styles.colors.color10}50`}`,
                '&:hover': {
                  backgroundColor: isActive ? `${styles.colors.color45}08` : `${styles.colors.color10}22`,
                },
              }}
              onClick={() => handleClick(q.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleClick(q.id)
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, pl: '12px' }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? styles.colors.color45 : styles.colors.color13,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {q.label}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', ml: '12px', pr: '12px' }}>
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: isActive ? styles.colors.color45 : styles.colors.color9,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('dashboard.storePerformance.executionCount', { count: executionCount })}
                </Typography>
                <ChevronRightIcon
                  sx={{
                    fontSize: '24px',
                    color: isActive ? styles.colors.color45 : styles.colors.color9,
                  }}
                />
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

const SelectedQuestionnairesForVersionsMemo = memo(SelectedQuestionnairesForVersions)
export { SelectedQuestionnairesForVersionsMemo as SelectedQuestionnairesForVersions }
